import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, ActivatedRouteSnapshot } from '@angular/router';

import { formatDate } from 'date-fns';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { DialogCloseReason, DialogService } from '@sui/dialog';

import { LoggerService } from '@services/logger.service';
import { ObjectEditorService } from './object-editor.service';
import { ObjectCreatorService } from './object-creator.service';
import { UserFacadeService } from '@services/user-facade.service';
import { SidebarService } from './sidebar.service';
import { FeedAlgorithmsService } from './feed-algorithms.service';
import { FeedDimensionsService } from './feed-dimensions.service';

import { ModuleOption } from '../components/module-switch/module-switch.component';
import { PickerDataobjectsDialogComponent } from '../components/popups/picker-dataobjects-dialog/picker-dataobjects-dialog.component';

import {
  PopupType,
  NomObjectClass,
  NomObjectTypes,
  NomDateTimeFormats,
  DimensionJoinTypeId,
  DimensionAttachTypeId,
} from '../constants';
import { Dashboard, Feed, ObjectDefinition, User } from '../models/core.models';
import { getIconForDataObject } from 'src/app/shared/utils/object-utils';
import { AppFeatures } from '../app.features';

import {
  activateFilters,
  deselectFilterItem,
  toggleFilterDimension,
} from '../actions/feed.actions';

import {
  selectUser,
  selectFeed,
  selectCurrentObject,
  selectCoreActiveModule,
  selectFeedActiveFilterItems,
} from '../reducers';
import {
  isValueItem,
  isPeriodItem,
  AnyFilterItem,
} from '../reducers/feed.reducer';
import { AppState } from 'src/app/reducers';

import {
  tap,
  take,
  filter,
  combineLatest,
  BehaviorSubject,
  distinctUntilChanged,
} from 'rxjs';

export interface BreadcrumbItem {
  key?: string; // * Unique key for the breadcrumb item
  label: string;
  kind: 'filter' | 'dimension' | 'period' | 'object' | 'navigation';
  url?: string | Array<any>;
  icon?: IconProp;
  backgroundColor?: string;
  className?: string;
  isDropdown?: boolean;
  action?: (remove?: boolean) => void;
  dropdownItems?: BreadcrumbDropdownItem[];
  collapsed?: boolean;
  tooltip?: string;
  autoRefresh?: boolean;
  autoRefreshSeconds?: number;
  algorithmCount?: number;
  algorithmAction?: () => void;
  isPaused?: boolean;
  pausedTimestamp?: number;
}

export interface BreadcrumbDropdownItem {
  label?: string;
  url?: string;
  icon?: IconProp;
  isSeparator?: boolean;
  action?: () => void;
}

@Injectable({
  providedIn: 'root',
})
export class BreadcrumbService {
  private routeBreadcrumbItems$ = new BehaviorSubject<BreadcrumbItem[]>([]);
  private stateBreadrumbItems$ = new BehaviorSubject<BreadcrumbItem[]>([]);
  // Filter breadcrumbs published by non-feed screens (e.g. the HTTP request log
  // explorer), appended after the route + feed-state items.
  private customStateBreadcrumbItems$ = new BehaviorSubject<BreadcrumbItem[]>(
    [],
  );
  newDimensionJoinType: number;
  additionalClass: string = '';

  prevRouteContext: string = '';

  public currentTab: string = '';

  currentActiveModule: ModuleOption;

  protected destroyRef = inject(DestroyRef);
  private readonly feedAlgorithms = inject(FeedAlgorithmsService);
  private readonly feedDimensions = inject(FeedDimensionsService);
  private dialogService = inject(DialogService);
  private sidebarService = inject(SidebarService);

  constructor(
    private store: Store<AppState>,
    private translateService: TranslateService,
    private editService: ObjectEditorService,
    private loggerService: LoggerService,
    private userFacade: UserFacadeService,
    private createService: ObjectCreatorService,
  ) {
    this.store
      .pipe(
        // select the active module from the store
        select(selectCoreActiveModule),
        // only update when the module actually changes
        distinctUntilChanged(),
        // side‑effect: assign to the component property
        tap((activeModule) => {
          this.currentActiveModule = activeModule;
        }),
        // auto‑unsubscribe when the component is destroyed
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // * Updates Feed title, Icon, and Dropdowns in Route Breadcrumbs
    // * based on Feed and User changes.
    // * Affects route-based breadcrumbs (routeBreadcrumbItems$).
    combineLatest([
      this.store.pipe(select(selectCurrentObject)),
      this.store.pipe(
        select(selectUser),
        filter((user): user is User => !!user),
      ),
      // * null on dashboard routes - the dashboard label still has to follow a rename
      this.store.pipe(select(selectFeed)),
      toObservable(this.feedAlgorithms.attached),
    ])
      .pipe(
        // only proceed when we have a valid feed
        filter(([object]) => !!object),
        // avoid re-running when neither feed nor user has changed
        distinctUntilChanged(
          (
            [prevObject, prevUser, prevFeed, prevAttachedAlgorithms],
            [currObject, currUser, currFeed, currAttachedAlgorithms],
          ) =>
            prevObject.id === currObject.id &&
            prevObject.title === currObject.title &&
            prevUser.id === currUser.id &&
            prevFeed?.dimensions === currFeed?.dimensions &&
            prevFeed?.is_paused === currFeed?.is_paused &&
            prevFeed?.timestamp_paused === currFeed?.timestamp_paused &&
            prevFeed?.is_auto_refresh === currFeed?.is_auto_refresh &&
            prevFeed?.auto_refresh_seconds === currFeed?.auto_refresh_seconds &&
            prevAttachedAlgorithms.length === currAttachedAlgorithms.length,
        ),
        tap(([object, user, feed, attachedAlgorithms]) => {
          const currentItems = this.routeBreadcrumbItems$.getValue();
          const objectKey =
            object.object_class_id === NomObjectClass.FEED
              ? 'feed'
              : object.object_class_id === NomObjectClass.DASHBOARD
                ? 'dashboard'
                : null;

          if (!objectKey) return;
          // * a feed route patches feed actions too - wait for the loaded feed
          if (objectKey === 'feed' && !feed) return;

          // * a page URL renders the dashboard as 'dashboard-with-page'
          const index = currentItems.findIndex(
            (item) =>
              item.key === objectKey ||
              (objectKey === 'dashboard' && item.key === 'dashboard-with-page'),
          );
          if (index === -1) return;

          const updatedItems = [...currentItems];
          updatedItems[index] = {
            ...updatedItems[index],
            label: this.withPageSuffix(updatedItems[index], object.title),
            icon: getIconForDataObject(
              object.object_class_id,
              object.object_type_id,
            ),
            dropdownItems:
              objectKey === 'feed'
                ? this.getFeedActions(feed as Feed, user)
                : this.getDashboardActions(),
          };

          if (objectKey === 'feed' && feed) {
            updatedItems[index] = {
              ...updatedItems[index],
              autoRefresh: feed.is_auto_refresh,
              autoRefreshSeconds: feed.auto_refresh_seconds,
              algorithmCount: attachedAlgorithms.length,
              algorithmAction: () => this.openAlgorithmsSidebar(feed as Feed),
              isPaused: feed.is_paused,
              pausedTimestamp: feed.timestamp_paused,
            };
          }

          this.routeBreadcrumbItems$.next(updatedItems);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  /**
   * A 'dashboard-with-page' item reads "Dashboard title / Page X"; a rename
   * replaces the title and keeps the page part.
   */
  private withPageSuffix(
    item: { key?: string; label?: string },
    title: string,
  ): string {
    if (item.key !== 'dashboard-with-page' || !item.label) {
      return title;
    }
    const separator = item.label.indexOf(' / ');
    return separator === -1 ? title : title + item.label.slice(separator);
  }

  // * Builds and updates filter and dimension breadcrumb items reactively
  // * - based on Feed, active Filters, and User changes.
  // * affects state-based breadcrumbs (stateBreadrumbItems$).
  subscribeForFeedStateUpdates() {
    combineLatest([
      this.store.pipe(select(selectFeedActiveFilterItems)),
      this.store.pipe(select(selectFeed)),
      this.store.pipe(
        select(selectUser),
        filter((user): user is User => !!user),
        distinctUntilChanged((a, b) => a.id === b.id),
      ),
    ])
      .pipe(
        tap(([activeFilterItems, feed, user]) => {
          const items: BreadcrumbItem[] = [];

          // * return if no Feed and clear the Breadcrumb items
          if (!feed) {
            this.stateBreadrumbItems$.next(items);
            return;
          }

          const hasPerm = (perm: AppFeatures) =>
            user.feature_components?.[perm] === true;

          const filterDimensions = feed.dimensions.filter(
            (d) => d.attach_type_id === DimensionAttachTypeId.FILTER,
          );
          const filterDimensionCount = filterDimensions.length;
          // build dimension items
          filterDimensions.forEach((dimension) => {
            const dropdownItems = [];

            if (hasPerm(AppFeatures.BREADCRUMB_DIMENSION_MENU_EDIT_DIMENSION)) {
              dropdownItems.push({
                icon: ['fas', 'gear'],
                label: this.translateService.instant(
                  'breadcrumb.label.edit-dimension',
                ),
                action: () =>
                  this.editService.loadFeedAndOpenPopup(dimension.id),
              });
            }

            if (
              hasPerm(
                AppFeatures.BREADCRUMB_DIMENSION_MENU_TOGGLE_NOT_DIMENSION,
              )
            ) {
              dropdownItems.push({
                icon: ['fas', 'redo'],
                label: this.translateService.instant(
                  'breadcrumb.label.toggle-join-type',
                ),
                action: () => {
                  const newType =
                    dimension.join_type_id === DimensionJoinTypeId.AND_NOT
                      ? DimensionJoinTypeId.AND
                      : DimensionJoinTypeId.AND_NOT;
                  this.feedDimensions.setJoinType(
                    feed.id,
                    dimension.id,
                    newType,
                  );
                },
              });
            }

            if (
              hasPerm(AppFeatures.BREADCRUMB_DIMENSION_MENU_REMOVE_DIMENSIONS)
            ) {
              dropdownItems.push({
                icon: ['fas', 'filter-circle-xmark'],
                label: this.translateService.instant(
                  'breadcrumb.label.detach-dimension',
                ),
                action: () => this.feedDimensions.detach(feed.id, dimension.id),
              });
            }

            items.push({
              label: dimension.title,
              kind: 'dimension',
              icon: getIconForDataObject(
                dimension.object_class_id,
                dimension.object_type_id,
              ),
              className:
                dimension.join_type_id === DimensionJoinTypeId.AND_NOT
                  ? 'dimension not-active'
                  : 'dimension',
              isDropdown: dropdownItems.length > 0,
              dropdownItems,
              collapsed: filterDimensionCount > 2,
            });
          });

          // build filter items
          activeFilterItems.forEach((filterItem: AnyFilterItem) => {
            if (isValueItem(filterItem)) {
              // this.loggerService.debug(
              //   'Adding value filter breadcrumb',
              //   filterItem,
              // );
              items.push({
                label: filterItem.title,
                kind: 'filter',
                icon: ['fas', 'filter'],
                className: 'filter',
                tooltip: filterItem.filter_title,
                action: (remove: boolean = false) => {
                  if (remove === true) {
                    this.store.dispatch(
                      deselectFilterItem({
                        propertyUri: filterItem.property_uri,
                        filterItem,
                        mode: 'include',
                      }),
                    );
                  }
                  this.store.dispatch(activateFilters());
                },
              });
            }

            if (isPeriodItem(filterItem)) {
              items.push({
                label:
                  formatDate(
                    filterItem.time_start * 1000,
                    NomDateTimeFormats.ANALYTICS_LONG,
                  ) +
                  ' - ' +
                  formatDate(
                    filterItem.time_end * 1000,
                    NomDateTimeFormats.ANALYTICS_LONG,
                  ),
                kind: 'period',
                icon: ['fas', 'calendar-alt'],
                className: 'period',
                isDropdown: false,
                action: (remove: boolean = false) => {
                  if (remove === true) {
                    this.store.dispatch(
                      deselectFilterItem({
                        propertyUri: filterItem.property_uri,
                        filterItem,
                        mode: 'include',
                      }),
                    );
                  }
                  this.store.dispatch(activateFilters());
                },
              });
            }
          });

          this.loggerService.debug('breadcrumb items', items);
          this.stateBreadrumbItems$.next(items);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private getBreadcrumbContext(activatedRoute: ActivatedRoute): string | null {
    let currentRoute = activatedRoute.root;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
      const context = currentRoute.snapshot.data?.['breadcrumbContext'];
      if (context) {
        return context;
      }
    }
    return null;
  }

  setItems(items: BreadcrumbItem[]) {
    this.routeBreadcrumbItems$.next(items);
  }

  getRouterItems() {
    return this.routeBreadcrumbItems$.asObservable();
  }

  getStateItems() {
    return this.stateBreadrumbItems$.asObservable();
  }

  getCustomStateItems() {
    return this.customStateBreadcrumbItems$.asObservable();
  }

  /** Publish filter breadcrumb items from a non-feed screen. */
  setCustomStateItems(items: BreadcrumbItem[]) {
    this.customStateBreadcrumbItems$.next(items);
  }

  clearCustomStateItems() {
    this.customStateBreadcrumbItems$.next([]);
  }

  addItem(item: BreadcrumbItem) {
    const currentItems = this.routeBreadcrumbItems$.getValue();
    this.routeBreadcrumbItems$.next([...currentItems, item]);
  }

  removeItem(label: string) {
    const currentItems = this.routeBreadcrumbItems$.getValue();
    const filteredItems = currentItems.filter((item) => item.label !== label);
    this.routeBreadcrumbItems$.next(filteredItems);
  }

  clearItems() {
    this.routeBreadcrumbItems$.next([]);
  }

  createUrl(routeSnapshot: ActivatedRouteSnapshot): string {
    const segments = routeSnapshot.pathFromRoot
      .map((v) => v.url.map((segment) => segment.toString()).join('/'))
      .filter((path) => path);
    return '/' + segments.join('/');
  }

  updateBreadcrumb(activatedRoute: ActivatedRoute, user: User): void {
    const breadcrumbs: BreadcrumbItem[] = [];

    let currentRoute = activatedRoute.root;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
      const { path, data: routeConfigData } = currentRoute.routeConfig;
      const { params, data } = currentRoute.snapshot;

      if (routeConfigData?.['breadcrumb']) {
        const breadcrumb: string = routeConfigData['breadcrumb'];
        let label = '';
        if (breadcrumb?.startsWith('resolve:')) {
          const resolvePattern = breadcrumb.split(':')[1];
          const [objectName, propertyName] = resolvePattern.split('.');

          // Retrieve the resolved object using the object name
          const resolvedObject = currentRoute.snapshot.data[objectName];
          label = resolvedObject?.[propertyName] || 'Unknown';
        } else if (breadcrumb === ':tab') {
          label = this.currentTab;
        } else {
          label = this.translateService.instant(routeConfigData['breadcrumb']);
        }

        if (label.length > 0) {
          this.addBreadcrumbItem(breadcrumbs, {
            key: path,
            label: label,
            kind: 'navigation',
            url: this.createUrl(currentRoute.snapshot),
            icon: routeConfigData?.['breadcrumbIcon'],
          });
        }
      } else {
        if (path) {
          switch (this.prevRouteContext) {
            case 'feeds':
              this.addBreadcrumbItem(breadcrumbs, {
                key: 'feeds',
                kind: 'navigation',
                label: this.translateService.instant('breadcrumb.label.feeds'),
                icon: ['fas', 'rss'],
                url: '/analytics/feeds',
              });
              break;
            case 'alerts':
              this.addBreadcrumbItem(breadcrumbs, {
                key: 'alerts',
                kind: 'navigation',
                label: this.translateService.instant('breadcrumb.label.alerts'),
                icon: ['fas', 'bell'],
                url: '/analytics/alerts',
              });
              break;

            case 'dashboards':
              this.addBreadcrumbItem(breadcrumbs, {
                key: 'dashboards',
                kind: 'navigation',
                label: this.translateService.instant(
                  'breadcrumb.label.dashboards',
                ),
                icon: ['fas', 'tachometer-alt'],
                url: '/analytics/dashboards',
              });
              break;

            default:
              if (data['workspace']) {
                this.addBreadcrumbItem(breadcrumbs, {
                  key: 'workspace',
                  kind: 'navigation',
                  label: data['workspace'].title,
                  className: 'workspace',
                  icon: data['workspace'].icon,
                  url: ['/analytics/workspace', params['workspaceId']],
                });
              }
          }
          if (params['dashboardId'] && data['dashboard']) {
            this.addDashboardBreadcrumb(breadcrumbs, data, currentRoute);
          }
          if (params['feedId'] && data['feed']) {
            this.addFeedBreadcrumb(breadcrumbs, currentRoute.snapshot, user);
          }
        }
      }
    }

    this.setItems(breadcrumbs);

    const context = this.getBreadcrumbContext(activatedRoute);
    this.prevRouteContext = context;
  }

  private addDashboardBreadcrumb(
    breadcrumbs: BreadcrumbItem[],
    data,
    currentRoute: ActivatedRoute,
  ) {
    const dashboardBaseUrl =
      '/analytics/workspace/' +
      data['dashboard']?.workspace?.id +
      '/dashboard/' +
      data['dashboard']?.id;

    const isOnPageUrl = this.isCurrentlyOnDashboardPage(currentRoute);

    if (isOnPageUrl) {
      // * when on a page URL, show "Dashboard Name / Page X" with navigation + dropdown
      const pageInfo = this.getCurrentPageInfo(currentRoute, data['dashboard']);

      this.addBreadcrumbItem(breadcrumbs, {
        key: 'dashboard-with-page',
        kind: 'navigation',
        label: `${data['dashboard'].title} / ${pageInfo}`,
        icon: data['dashboard'].icon,
        className: 'dashboard',
        url: dashboardBaseUrl, // ! Navigate to base URL
        isDropdown: false,
        dropdownItems: this.getDashboardActions(),
      });
    } else {
      // * when on base URL, show just "Dashboard Name" with dropdown only
      this.addBreadcrumbItem(breadcrumbs, {
        key: 'dashboard',
        kind: 'navigation',
        label: data['dashboard'].title,
        icon: data['dashboard'].icon,
        className: 'dashboard',
        url: dashboardBaseUrl, // ! Navigate to base URL
        isDropdown: true,
        dropdownItems: this.getDashboardActions(),
      });
    }
  }

  private addBreadcrumbItem(
    breadcrumbs: BreadcrumbItem[],
    item: BreadcrumbItem,
  ): void {
    breadcrumbs.push(item);
  }

  private isCurrentlyOnDashboardPage(route: ActivatedRoute): boolean {
    let currentRoute = route;
    while (currentRoute) {
      if (currentRoute.snapshot.params['pageId']) {
        return true;
      }
      if (currentRoute.firstChild) {
        currentRoute = currentRoute.firstChild;
      } else {
        break;
      }
    }
    return false;
  }

  private getCurrentPageInfo(route: ActivatedRoute, dashboard: any): string {
    let currentRoute = route;
    let pageId: string | null = null;

    while (currentRoute) {
      if (currentRoute.snapshot.params['pageId']) {
        pageId = currentRoute.snapshot.params['pageId'];
        break;
      }
      if (currentRoute.firstChild) {
        currentRoute = currentRoute.firstChild;
      } else {
        break;
      }
    }

    if (pageId && dashboard?.pages) {
      const page = dashboard.pages.find((p) => p.id.toString() === pageId);
      if (page) {
        return `${page.name}`;
      }
    }

    return 'Page N/A';
  }

  private addFeedBreadcrumb(
    breadcrumbs: BreadcrumbItem[],
    route: ActivatedRouteSnapshot,
    user: User,
  ): void {
    let feed = route.data['feed'];

    const breadcrumb: BreadcrumbItem = {
      key: 'feed',
      kind: 'object',
      label: feed.title,
      icon: feed.icon,
      className: 'feed',
      url: this.createUrl(route),
      isDropdown: true,
      action: () => console.log('Feed action'),
      dropdownItems: this.getFeedActions(feed, user),
      autoRefresh: feed.is_auto_refresh,
      autoRefreshSeconds: feed.auto_refresh_seconds,
      isPaused: feed.is_paused,
      pausedTimestamp: feed.timestamp_paused,
    };
    breadcrumbs.push(breadcrumb);
  }

  private getFeedActions(feed: Feed, user: User): BreadcrumbDropdownItem[] {
    const staticActions: BreadcrumbDropdownItem[] = [];

    // Helper to check if the user has a specific permission.
    const hasPermission = (permission: AppFeatures): boolean =>
      user?.feature_components?.[permission] === true;

    // Add "Create New Dimension and Link" if permitted.
    if (
      hasPermission(AppFeatures.BREADCRUMB_CONTEXT_MENU_CREATE_NEW_AND_LINK) &&
      this.userFacade.hasAllObjectPermissionsSync(feed, ['can_edit'])
    ) {
      staticActions.push({
        icon: ['fas', 'link'],
        label: this.translateService.instant(
          'breadcrumb.label.create-new-dimension-and-link',
        ),
        action: this.createNewDimensionAndAddAsPivot.bind(this),
      });
    }

    // Add "Edit Linked Dimensions" if permitted.
    if (
      hasPermission(
        AppFeatures.BREADCRUMB_CONTEXT_MENU_EDIT_LINKED_DIMENSIONS,
      ) &&
      this.userFacade.hasAllObjectPermissionsSync(feed, ['can_edit'])
    ) {
      staticActions.push({
        icon: ['fas', 'edit'],
        label: this.translateService.instant(
          'breadcrumb.label.edit-linked-dimensions',
        ),
        action: this.editLinkedDimensions.bind(this),
      });
    }

    // Add separator if any of the above actions were added.
    if (staticActions.length > 0) {
      staticActions.push({ isSeparator: true });
    }

    // Add "Create New Dimension Add To Favourites" if permitted.
    if (
      hasPermission(
        AppFeatures.BREADCRUMB_CONTEXT_MENU_CREATE_NEW_AND_ADD_TO_FAVORITES,
      ) &&
      this.userFacade.hasAllObjectPermissionsSync(feed, ['can_edit'])
    ) {
      staticActions.push({
        icon: ['fas', 'plus'],
        label: this.translateService.instant(
          'breadcrumb.label.create-new-dimension-add-to-favourites',
        ),
        action: this.createNewDimensionAddToFeedFilters.bind(this),
      });
    }

    // Add "Edit Favourite Dimensions" if permitted.
    if (
      hasPermission(AppFeatures.BREADCRUMB_CONTEXT_MENU_EDIT_FAVORITES) &&
      this.userFacade.hasAllObjectPermissionsSync(feed, ['can_edit'])
    ) {
      staticActions.push({
        icon: ['fas', 'edit'],
        label: this.translateService.instant(
          'breadcrumb.label.edit-favourite-dimensions',
        ),
        action: this.editFavouriteDimensions.bind(this),
      });
    }

    // Add a separator if any actions have been added so far.
    if (staticActions.length > 0) {
      staticActions.push({ isSeparator: true });
    }

    // Add "Edit Feed" if permitted (assumed permission here is FEED_SETTINGS_ROOT; update as needed).
    if (
      hasPermission(AppFeatures.FEED_SETTINGS_ROOT) &&
      this.userFacade.hasAllObjectPermissionsSync(feed, ['can_edit'])
    ) {
      staticActions.push({
        icon: ['fas', 'cog'],
        label: this.translateService.instant(
          'breadcrumb.label.edit-feed-settings',
        ),
        action: this.editFeed.bind(this),
      });
      if (feed.object_type_id !== NomObjectTypes.CLIPPING_FEED) {
        staticActions.push({
          icon: ['fas', 'cog'],
          label: this.translateService.instant(
            'breadcrumb.label.edit-query-tab',
          ),
          action: () => this.editFeed('search-builder'),
        });
      }
    }

    // "Refresh" is available to all users.
    staticActions.push({
      icon: ['fas', 'redo'],
      label: this.translateService.instant('breadcrumb.label.refresh'),
      action: () => this.objectRefresh(),
    });

    // Filter and map dynamic actions based on dimensions of type FILTER.
    const dynamicActions: BreadcrumbDropdownItem[] =
      feed.dimensions
        ?.filter(
          (dimension) =>
            dimension.attach_type_id === DimensionAttachTypeId.FAVORITE,
        )
        .map((dimension) => ({
          icon: ['fas', 'filter'],
          label: dimension.title,
          action: () => {
            this.store.dispatch(
              toggleFilterDimension({
                dimension,
                activate: true,
                isPivot: false,
              }),
            );
          },
        })) || [];

    // Add a separator before dynamic actions if there are any.
    if (dynamicActions.length > 0) {
      staticActions.push({ isSeparator: true });
      staticActions.push(...dynamicActions);
    }

    return staticActions;
  }

  private getDashboardActions(): any[] {
    return [
      {
        icon: ['fas', 'cog'],
        label: this.translateService.instant('breadcrumb.label.edit-dashboard'),
        action: this.editDashboard.bind(this),
      },
      {
        icon: ['fas', 'redo'],
        label: this.translateService.instant('breadcrumb.label.refresh'),
        action: this.objectRefresh.bind(this),
      },
    ];
  }

  private createNewDimensionAndAddAsPivot(): void {
    // TODO: Implement logic to create a new dimension and link
    this.store
      .pipe(
        // select the current feed
        select(selectFeed),
        // only continue when feed is available
        filter((feed): feed is Feed => !!feed),
        // take one then complete
        take(1),
        // side‑effect: create the new filter dimension
        tap((feed) =>
          this.createService.createNewDimension('', {
            object_id: feed.id,
            object_class_id: NomObjectClass.FEED,
            attach_type_id: DimensionAttachTypeId.FILTER,
          }),
        ),
        // cleanup on destroy
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private createNewDimensionAddToFeedFilters(): void {
    // TODO: Implement logic to create a new dimension and add to favourites
    this.store
      .pipe(
        // select the current feed
        select(selectFeed),
        // only proceed when feed is non-null
        filter((feed): feed is Feed => !!feed),
        // take exactly one emission then complete
        take(1),
        // side‑effect: create the new favorite dimension
        tap((feed) =>
          this.createService.createNewDimension('', {
            object_id: feed.id,
            object_class_id: NomObjectClass.FEED,
            attach_type_id: DimensionAttachTypeId.FAVORITE,
          }),
        ),
        // automatically unsubscribe on component destroy
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private editFeed(tabId?: string): void {
    this.store
      .pipe(
        // select the current feed
        select(selectFeed),
        // skip null/undefined feeds
        filter((feed): feed is Feed => !!feed),
        // take the first valid emission then complete
        take(1),
        // side‑effect: load the feed and open the popup
        tap((feed) => this.editService.loadFeedAndOpenPopup(feed.id, tabId)),
        // auto‑unsubscribe if the component is destroyed first
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private editDashboard(): void {
    this.store
      .pipe(
        // select the current dashboard object
        select(selectCurrentObject),
        // skip null/undefined values
        filter((dashboard): dashboard is Dashboard => !!dashboard),
        // take the first valid emission, then complete
        take(1),
        // side‑effect: invoke the edit service
        tap((dashboard) =>
          this.editService.loadDashboardAndOpenPopup(dashboard.id),
        ),
        // automatically unsubscribe on component destroy
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private openDimensionPicker(
    feed: Feed,
    attachTypeId: DimensionAttachTypeId,
  ): void {
    const selectedObjectIds = new Map<string, ObjectDefinition>(
      feed.dimensions
        ?.filter((d) => d.attach_type_id === attachTypeId)
        .map((d) => [
          d.id,
          {
            object_class_id: d.object_class_id,
            object_type_id: d.object_type_id,
            object_id: d.id,
          } as ObjectDefinition,
        ]) ?? [],
    );

    const popupType =
      attachTypeId === DimensionAttachTypeId.FILTER
        ? PopupType.ATTACH_DIMENSIONS_AS_FILTERS
        : PopupType.SELECT_FAVORITE_DIMENSIONS;

    const dialogRef = this.dialogService.openTabsDialog(
      PickerDataobjectsDialogComponent,
      {
        data: {
          selectedObjectIds,
          objectTypeIds: [NomObjectTypes.SEARCH_DIMENSION],
          popupType,
        },
        width: '61vw',
        minWidth: 1220,
        maxWidth: 1550,
        height: '70vh',
        closeOnClick: false,
        closeOnEscape: true,
      },
    );

    dialogRef.closed
      .pipe(
        take(1),
        filter(
          (event: any) =>
            event?.reason === DialogCloseReason.Save ||
            event?.reason === DialogCloseReason.SaveAndClose,
        ),
      )
      .subscribe((event: any) => {
        this.feedDimensions.attach(feed.id, event.data, attachTypeId);
      });
  }

  private editFavouriteDimensions(): void {
    this.store
      .pipe(
        // select the current feed
        select(selectFeed),
        // only proceed if we actually have a Feed object
        filter((feed): feed is Feed => !!feed),
        // take the first emission and complete
        take(1),
        // side‑effect: open the dimension picker for favorites
        tap((feed) =>
          this.openDimensionPicker(feed, DimensionAttachTypeId.FAVORITE),
        ),
        // safety‑net unsubscribe if the component is destroyed first
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private editLinkedDimensions(): void {
    this.store
      .pipe(
        // select the current feed from the store
        select(selectFeed),
        // only proceed when we have a non-null feed
        filter((feed): feed is Feed => !!feed),
        // take the first valid emission and complete
        take(1),
        // side‑effect: open the filter dimension picker
        tap((feed) =>
          this.openDimensionPicker(feed, DimensionAttachTypeId.FILTER),
        ),
        // auto‑unsubscribe if the component is destroyed first
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  objectRefresh() {
    this.store.dispatch(activateFilters());
  }

  private openAlgorithmsSidebar(feed: Feed): void {
    const label = this.translateService.instant(
      'sidebar-tools.label.algorithms',
    );
    this.sidebarService.selectComponent(
      {
        key: 'algorithms',
        label,
        header: label,
        componentName: 'AlgorithmsComponent',
        icon: ['fas', 'microchip'],
        inputs: {
          objectId: feed.id,
          objectClassId: NomObjectClass.FEED,
        },
      },
      true,
    );
  }
}
